import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import LocationAutocomplete from "./LocationAutocomplete";

interface IRouteControlsProps {
  onRouteChange: (route: {
    start: google.maps.places.PlaceResult | null;
    end: google.maps.places.PlaceResult | null;
  }) => void;
}

function RouteControls({ onRouteChange }: IRouteControlsProps) {
  const [startLocation, setStartLocation] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [endLocation, setEndLocation] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [showEndLocation, setShowEndLocation] = useState<boolean>(false);
  const geocodingLibrary = useMapsLibrary("geocoding");

  useEffect(() => {
    if (!geocodingLibrary) return;

    // Load place IDs from localStorage on mount
    const savedStartPlaceId = localStorage.getItem("startPlaceId");
    const savedEndPlaceId = localStorage.getItem("endPlaceId");

    const geocoder = new geocodingLibrary.Geocoder();

    if (savedStartPlaceId) {
      geocoder.geocode({ placeId: savedStartPlaceId }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          setStartLocation(results[0]);
        }
      });
    }

    if (savedEndPlaceId) {
      geocoder.geocode({ placeId: savedEndPlaceId }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          setEndLocation(results[0]);
          setShowEndLocation(true);
        }
      });
    }
  }, [geocodingLibrary]);

  useEffect(() => {
    // Save place IDs to localStorage whenever they change
    if (startLocation) {
      localStorage.setItem("startPlaceId", startLocation.place_id || "");
    }

    if (endLocation) {
      localStorage.setItem("endPlaceId", endLocation.place_id || "");
    }
  }, [startLocation, endLocation]);

  useEffect(() => {
    onRouteChange({
      start: startLocation,
      end: showEndLocation ? endLocation : null,
    });
  }, [startLocation, endLocation, showEndLocation, onRouteChange]);

  const handleStartLocationSelected = (
    place: google.maps.places.PlaceResult | null,
  ) => {
    setStartLocation(place);
  };

  const handleEndLocationSelected = (
    place: google.maps.places.PlaceResult | null,
  ) => {
    setEndLocation(place);
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Route Settings
      </Typography>

      <LocationAutocomplete
        label="Start Location"
        placeholder="Enter start point"
        onPlaceSelected={handleStartLocationSelected}
        value={startLocation?.formatted_address || ""} // Prefill with stored data
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={showEndLocation}
            onChange={e => setShowEndLocation(e.target.checked)}
            size="small"
          />
        }
        label="Specify custom end location"
      />

      {showEndLocation && (
        <LocationAutocomplete
          label="End Location"
          placeholder="Enter end point"
          onPlaceSelected={handleEndLocationSelected}
          value={endLocation?.formatted_address || ""} // Prefill with stored data
        />
      )}
    </Box>
  );
}

export default RouteControls;
